import React, { useMemo, useCallback } from 'react';
import classNames from 'classnames';
import { HeaderPanel } from './../HeaderPanel';
import { IHeaderPanelActionProps } from './../HeaderPanelAction';
import { AssignmentField } from './../assignments/AssignmentField';
import { ApprovalLevelDto } from '@decisions/server/DecisionsFramework/ServiceLayer/Services/Approvals/ApprovalLevelDto';
import './ApprovalLevel.less';
import { IconButton } from 'components/buttons/IconButton';
import { search } from 'components/assignments/utils/AssignmentUtils';
import { AssigneeType, AssigneeDto } from '@decisions/server/DecisionsFramework/ServiceLayer/Services/Assignments';
import { humanize } from './ApprovalChainUtils';
import { HtmlRenderer } from 'components/HtmlRenderer/HtmlRenderer';

export interface IApprovalLevelProps extends React.HTMLProps<HTMLLIElement> {
  canMoveUp: boolean;
  canMoveDown: boolean;
  canDelete: boolean;
  level: ApprovalLevelDto;
  levelNumber: number;
  allowRules?: boolean;
  allowExpiration?: boolean;
  onLevelDown: (levelNumber: number, level: ApprovalLevelDto) => void;
  onLevelUp: (levelNumber: number, level: ApprovalLevelDto) => void;
  onDelete: (levelNumber: number, level: ApprovalLevelDto) => void;
  onAssigneesChanged: (assignees: AssigneeDto[], levelNumber: number, level: ApprovalLevelDto) => void;
  onEditExpiration: (levelNumber: number, level: ApprovalLevelDto) => void;
  onEditRule: (levelNumber: number, level: ApprovalLevelDto) => void;
}

const getClassName = (className?: string) => classNames('dpc-approval-level', className);

function searchApprovers(text: string, loadedCount: number, pageSize: number) {
  return search(text, loadedCount, pageSize, AssigneeType.Account | AssigneeType.Group);
}

/**
 * Copyright (c) 2020 Decisions All Rights Reserved
 */
export function ApprovalLevel({
  level,
  canMoveDown,
  canMoveUp,
  allowRules,
  allowExpiration,
  levelNumber,
  canDelete,
  className,
  onLevelUp,
  onLevelDown,
  onDelete,
  onAssigneesChanged,
  onEditExpiration: onEditTimeToApprove,
  onEditRule,
  ...htmlOptions
}: IApprovalLevelProps) {
  const actions = useMemo(() => {
    const actions: IHeaderPanelActionProps[] = [];

    if (canMoveUp) {
      actions.push({
        iconClassName: 'dpc-approval-level__move-up',
        title: 'Move level up',
        onClick: () => onLevelUp(levelNumber, level),
      });
    }

    if (canMoveDown) {
      actions.push({
        iconClassName: 'dpc-approval-level__move-down',
        title: 'Move level down',
        onClick: () => onLevelDown(levelNumber, level),
      });
    }

    if (canDelete) {
      actions.push({
        iconClassName: 'dpc-approval-level__delete',
        title: 'Delete approval level',
        onClick: () => onDelete(levelNumber, level),
      });
    }

    return actions;
  }, [canDelete, canMoveDown, canMoveUp, level, levelNumber, onDelete, onLevelDown, onLevelUp]);

  const handleAssigneesChanged = useCallback(
    (assignees: AssigneeDto[]) => onAssigneesChanged(assignees, levelNumber, level),
    [level, levelNumber, onAssigneesChanged]
  );

  const handleEditTimeToApprove = useCallback(() => onEditTimeToApprove(levelNumber, level), [
    level,
    levelNumber,
    onEditTimeToApprove,
  ]);

  const handleEditRule = useCallback(() => onEditRule(levelNumber, level), [level, levelNumber, onEditRule]);

  return (
    <li className={getClassName(className)} {...htmlOptions}>
      <HeaderPanel
        title={`Level ${levelNumber}`}
        isCollapsible={true}
        isOpen={true}
        actions={actions}
        rootStyle={{ className: 'dpc-approval-level__panel dp-header-panel--default' }}
      >
        <div className="dpc-approval-level__body">
          <AssignmentField
            assignees={level.Approvers ?? []}
            search={searchApprovers}
            onChange={handleAssigneesChanged}
          />
          {allowRules && (
            <div className="dpc-approval-level__rule">
              <span className="dpc-approval-level__when-label">When</span>
              <IconButton
                className="dpc-approval-level__edit-rule"
                iconClassName="dpc-approval-level__edit-icon"
                iconSize="small"
                onClick={handleEditRule}
                title="Edit rule"
              />
              <HtmlRenderer
                component="span"
                className="dpc-approval-level__rule-value"
                html={level.RuleText ?? 'Always'}
              />
            </div>
          )}
          {allowExpiration && (
            <div className="dpc-approval-level__expiration">
              <span className="dpc-approval-level__expiration-label">Expires</span>
              <IconButton
                className="dpc-approval-level__edit-expiration"
                iconClassName="dpc-approval-level__edit-icon"
                iconSize="small"
                onClick={handleEditTimeToApprove}
                title="Edit expiration time"
              />
              <div className="dpc-approval-level__expiration-time">
                {level.TimeToApprove && <span>in </span>}
                <span className="dpc-approval-level__expiration-value">
                  {level.TimeToApprove ? humanize(level.TimeToApprove) : 'Never'}
                </span>
              </div>
            </div>
          )}
        </div>
      </HeaderPanel>
    </li>
  );
}
